import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { BordButton, BordOneToneIcon } from 'bord-design-system';
import NudosReportLogisticServiceProblem from '../../../../../../components/NudosReportLogisticServiceProblem/NudosReportLogisticServiceProblem';
import ServiceIncidentSidebar from '../../../../../../components/ServiceIncidentSidebar/ServiceIncidentSidebar';
import { getOpenIncidents } from '../../../../../../services/incidents.service';
import {
  INCIDENT_MODES,
  INCIDENT_TYPE_ID,
  TServiceIncidentSidebarMode
} from '../../../../../../types/incidents.types';
import { getLatestOpenLogisticReport } from '../../../../utils/general.utils';
import useOnlyDevelopers from 'state/setOnlyDevelopers';
import { IlogisticServiceDetails, TlogisticServiceStatus } from '../../../../../../types/services';
import DefinitiveEstimatedDates from './DefinitiveEstimatedDates';
import InitialEstimatedDates from './InitialEstimatedDates';
import { LOGISTIC_SERVICE_STATUS_NAME } from 'types/suscription/history.types';
import './LogisticServiceDatesAndProgrammingDetails.scss';

const LogisticServiceDatesAndProgrammingDetails = ({
  logisticServiceData,
  updateDataCallback
}: {
  logisticServiceData: IlogisticServiceDetails;
  updateDataCallback: () => void;
}) => {
  const { collectionInfo, deliveryInfo, statusName } = logisticServiceData;
  const { isOnlyDevsExperience } = useOnlyDevelopers();
  const { push } = useHistory();

  const HISTORY_TAB_ID = 4;

  const [sidebarMode, setSidebarMode] = useState<TServiceIncidentSidebarMode>();
  const [openIncidentsCount, setOpenIncidentsCount] = useState(0);

  const statusesWithInitialEstimatedDates: TlogisticServiceStatus[] = [
    LOGISTIC_SERVICE_STATUS_NAME.TO_CONFIRMED,
    LOGISTIC_SERVICE_STATUS_NAME.CONFIRMED,
    LOGISTIC_SERVICE_STATUS_NAME.IN_PROCESS
  ];

  const statusesWithReportLSProblem: TlogisticServiceStatus[] = [
    LOGISTIC_SERVICE_STATUS_NAME.IN_PROCESS,
    LOGISTIC_SERVICE_STATUS_NAME.CONFIRMED
  ];

  const statusesWithIncidentsActions: TlogisticServiceStatus[] = [
    LOGISTIC_SERVICE_STATUS_NAME.CONFIRMED,
    LOGISTIC_SERVICE_STATUS_NAME.IN_PROCESS,
    LOGISTIC_SERVICE_STATUS_NAME.ACTIVE
  ];

  const logisticServiceId = logisticServiceData?.id;
  const statusNameIsNotEmpty = statusName != null;
  const showInitialEstimatedDates = statusNameIsNotEmpty && statusesWithInitialEstimatedDates.includes(statusName);
  const displayReportLSProblem = statusNameIsNotEmpty && statusesWithReportLSProblem.includes(statusName);
  const canManageIncidents = statusNameIsNotEmpty && statusesWithIncidentsActions.includes(statusName);
  const showIncidentsEmptyState = statusName === LOGISTIC_SERVICE_STATUS_NAME.TO_CONFIRMED;
  const shouldRelocateActionsToTrackingCard = isOnlyDevsExperience && statusName === LOGISTIC_SERVICE_STATUS_NAME.ACTIVE;
  const hasOpenOperationalReport = getLatestOpenLogisticReport(logisticServiceData.reports) != null;
  const requesterComments = collectionInfo?.collectionComment || deliveryInfo?.deliveryComment;
  const showIncidentsActionsCard = isOnlyDevsExperience && (canManageIncidents || showIncidentsEmptyState) && !shouldRelocateActionsToTrackingCard;
  const trackingCardIncidentsActions = shouldRelocateActionsToTrackingCard
    ? {
        openIncidentsCount,
        onCloseIncidents: () => setSidebarMode(INCIDENT_MODES.CLOSE),
        onCreateIncident: () => setSidebarMode(INCIDENT_MODES.CREATE)
      }
    : undefined;

  const refreshOpenIncidentsCount = useCallback(async () => {
    if (!isOnlyDevsExperience || !logisticServiceId || !canManageIncidents) return;
    const incidents = await getOpenIncidents(
      logisticServiceId,
      [INCIDENT_TYPE_ID.BLOCKING, INCIDENT_TYPE_ID.CANCELLED, INCIDENT_TYPE_ID.ATTEMPTS]
    ).catch(() => null);
    if (incidents) setOpenIncidentsCount(incidents.length);
  }, [isOnlyDevsExperience, logisticServiceId, canManageIncidents]);

  const handleSidebarActionCompleted = () => {
    updateDataCallback();
    refreshOpenIncidentsCount();
    push(`/logistic-service/details/${logisticServiceId}`, { initialTab: HISTORY_TAB_ID });
  };

  useEffect(() => {
    refreshOpenIncidentsCount();
  }, [refreshOpenIncidentsCount]);

  return (
    <div className="LogisticServiceDatesAndProgrammingDetails">
      <div className="logisticServiceDetailsTab1SectionTitle">Detalles del servicio logístico</div>
      <div className="detailsSection">
        <div className="userInformationContainer">
          <div className="userInformationTitle">Información ingresada por el usuario</div>
          <InitialEstimatedDates logisticServiceData={logisticServiceData} />
          {requesterComments && (
            <>
              <div className="dividerStyles" />
              <div className="requesterCommentsSection">
                <div className="requesterCommentsTitle">Solicitudes del usuario</div>
                <div className="requesterCommentsText">{requesterComments}</div>
              </div>
            </>
          )}
        </div>

        {!isOnlyDevsExperience && (showInitialEstimatedDates || hasOpenOperationalReport) && (
          <NudosReportLogisticServiceProblem
            displayReportLSProblem={displayReportLSProblem}
            logisticServiceDetails={logisticServiceData}
            afterSubmitCallback={updateDataCallback}
          />
        )}

        {showIncidentsActionsCard && (
          <div className="incidentsActionsContainer">
            <div className="incidentsActionsHeader">
              <div className="incidentsActionsTitle">Reporte del servicio</div>
              <div className="incidentsActionsSubtitle">
                Notifica al cliente sobre actualizaciones importantes en el servicio.
                Esta información se enviará por correo con el detalle.
              </div>
            </div>
            {canManageIncidents ? (
              <div className="incidentsActionsButtons">
                <BordButton
                  label={`Cerrar reportes (${openIncidentsCount})`}
                  bordButtonStyle="outlined"
                  customClassName="incidentsActionsCloseButton"
                  disabled={openIncidentsCount === 0}
                  onClick={() => setSidebarMode(INCIDENT_MODES.CLOSE)}
                />
                <BordButton
                  label="Crear reporte del servicio"
                  bordButtonStyle="filled"
                  customClassName="incidentsActionsCreateButton"
                  onClick={() => setSidebarMode(INCIDENT_MODES.CREATE)}
                />
              </div>
            ) : (
              <div className="incidentsActionsEmpty">
                <BordOneToneIcon variant="layers" customWidth="5.2rem" />
                <span>Disponible cuando el servicio esté confirmado</span>
              </div>
            )}
          </div>
        )}

        {!showInitialEstimatedDates && (
          <div className="datesDetailsSection">
            <DefinitiveEstimatedDates
              logisticServiceData={logisticServiceData}
              updateDataCallback={updateDataCallback}
              incidentsActions={trackingCardIncidentsActions}
            />
          </div>
        )}
      </div>

      {isOnlyDevsExperience && (
        <ServiceIncidentSidebar
          open={!!sidebarMode}
          mode={sidebarMode ?? INCIDENT_MODES.CREATE}
          logisticServiceData={logisticServiceData}
          onClose={() => setSidebarMode(undefined)}
          onActionCompleted={handleSidebarActionCompleted}
        />
      )}
    </div>
  );
};

export default LogisticServiceDatesAndProgrammingDetails;
